import { CalendarDays, ChevronLeft, ChevronRight, Link2 } from "lucide-react";
import { useState } from "react";
import { useGetList } from "ra-core";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { Job } from "../../atomic-crm/jobs/jobModel";
import { ListPageHeader } from "../list/ListPageHeader";
import { NewAppointmentDialog } from "./NewAppointmentDialog";
import { BookingPagesDialog } from "./BookingPagesDialog";
import {
  addDays,
  DAY_END_HOUR,
  DAY_START_HOUR,
  formatTime,
  isSameDay,
  startOfWeek,
  type Appointment,
} from "./calendarModel";

/**
 * Workspace week calendar: appointments (solid navy) and scheduled jobs
 * (outlined) on one grid. All data is RLS-scoped; clicking an entry opens
 * the underlying record.
 */
export const CalendarPage = () => {
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const weekStart = anchor;
  const weekEnd = addDays(weekStart, 7);
  const navigate = useNavigate();

  const { data: appointments } = useGetList<Appointment>("appointments", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "starts_at", order: "ASC" },
    filter: {
      "starts_at@gte": weekStart.toISOString(),
      "starts_at@lt": weekEnd.toISOString(),
    },
  });

  const { data: jobs } = useGetList<Job>("jobs", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "scheduled_start", order: "ASC" },
    filter: {
      "scheduled_start@gte": weekStart.toISOString(),
      "scheduled_start@lt": weekEnd.toISOString(),
    },
  });

  // Tasks and invoices carry a DATE, not a time, so they live in an all-day
  // strip rather than being faked onto the hour grid at an invented time.
  const dateOnly = (d: Date) => d.toISOString().slice(0, 10);
  const { data: dueTasks } = useGetList<{
    id: number;
    due_date: string;
    status: string;
  }>("tasks", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "due_date", order: "ASC" },
    filter: {
      "due_date@gte": dateOnly(weekStart),
      "due_date@lt": dateOnly(weekEnd),
      "status@neq": "done",
    },
  });
  const { data: dueInvoices } = useGetList<{
    id: number;
    due_date: string;
    status: string;
    balance_cents: number;
  }>("invoices", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "due_date", order: "ASC" },
    filter: {
      "due_date@gte": dateOnly(weekStart),
      "due_date@lt": dateOnly(weekEnd),
      "status@in": "(issued,partially_paid,overdue)",
    },
  });

  const allDayFor = (day: Date) => {
    const key = dateOnly(day);
    const tasks = (dueTasks ?? []).filter(
      (task) =>
        task.due_date?.slice(0, 10) === key && task.status !== "cancelled",
    );
    const invoices = (dueInvoices ?? []).filter(
      (invoice) => invoice.due_date?.slice(0, 10) === key,
    );
    return { tasks: tasks.length, invoices: invoices.length };
  };

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const hours = Array.from(
    { length: DAY_END_HOUR - DAY_START_HOUR },
    (_, i) => DAY_START_HOUR + i,
  );
  const weekLabel = `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${addDays(weekStart, 6).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;

  const entryBlocks = (day: Date) => {
    const blocks: {
      key: string;
      title: string;
      time: string;
      startHour: number;
      durationHours: number;
      kind: "appointment" | "job";
      cancelled: boolean;
      onClick: () => void;
    }[] = [];
    for (const appointment of appointments ?? []) {
      const start = new Date(appointment.starts_at);
      if (!isSameDay(start, day)) continue;
      const end = new Date(appointment.ends_at);
      blocks.push({
        key: `a-${appointment.id}`,
        title: appointment.title,
        time: `${formatTime(appointment.starts_at)}, ${formatTime(appointment.ends_at)}`,
        startHour: start.getHours() + start.getMinutes() / 60,
        durationHours: Math.max(
          0.5,
          (end.getTime() - start.getTime()) / 3_600_000,
        ),
        kind: "appointment",
        cancelled: appointment.status === "cancelled",
        onClick: () => {},
      });
    }
    for (const job of jobs ?? []) {
      if (!job.scheduled_start) continue;
      const start = new Date(job.scheduled_start);
      if (!isSameDay(start, day)) continue;
      const end = job.scheduled_end
        ? new Date(job.scheduled_end)
        : new Date(start.getTime() + 3_600_000);
      blocks.push({
        key: `j-${job.id}`,
        title: `#${job.job_number} ${job.title}`,
        time: `${formatTime(job.scheduled_start)}`,
        startHour: start.getHours() + start.getMinutes() / 60,
        durationHours: Math.max(
          0.5,
          (end.getTime() - start.getTime()) / 3_600_000,
        ),
        kind: "job",
        cancelled: job.status === "cancelled",
        onClick: () => navigate(`/jobs/${job.id}/show`),
      });
    }
    return blocks;
  };

  const hourHeight = 44;

  return (
    <>
      <ListPageHeader
        title="Calendar"
        subtitle="Appointments, jobs, tasks and invoices due across your workspace"
        actions={
          <>
            <BookingPagesDialog />
            <NewAppointmentDialog />
          </>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          className="size-8"
          onClick={() => setAnchor(addDays(weekStart, -7))}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAnchor(startOfWeek(new Date()))}
        >
          Today
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="size-8"
          onClick={() => setAnchor(addDays(weekStart, 7))}
        >
          <ChevronRight className="size-4" />
        </Button>
        <span className="ml-1 flex items-center gap-1.5 text-[13.5px] font-medium text-foreground">
          <CalendarDays className="size-4 text-muted-foreground" />
          {weekLabel}
        </span>
        <span className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-sm bg-primary" />
            Appointments
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-sm border-2 border-primary/50" />
            Jobs
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-full bg-amber-500/30" />
            Tasks due
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-full bg-red-500/30" />
            Invoices due
          </span>
        </span>
      </div>

      <Card className="overflow-x-auto py-0">
        <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-border text-center">
          <div />
          {days.map((day) => (
            <div
              key={day.toISOString()}
              className={cn(
                "border-l border-border-subtle py-2",
                isSameDay(day, new Date()) && "bg-surface-muted",
              )}
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {day.toLocaleDateString(undefined, { weekday: "short" })}
              </p>
              <p
                className={cn(
                  "text-[15px] font-semibold",
                  isSameDay(day, new Date())
                    ? "text-primary"
                    : "text-foreground",
                )}
              >
                {day.getDate()}
              </p>
            </div>
          ))}
        </div>
        {/* All-day strip: date-based work (tasks due, invoices due). */}
        <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-border">
          <div className="py-1 pr-2 text-right text-[10px] uppercase tracking-wide text-muted-foreground">
            due
          </div>
          {days.map((day) => {
            const { tasks, invoices } = allDayFor(day);
            return (
              <div
                key={day.toISOString()}
                className={cn(
                  "flex min-h-7 flex-wrap items-center gap-1 border-l border-border-subtle px-1 py-1",
                  isSameDay(day, new Date()) && "bg-surface-muted",
                )}
              >
                {tasks > 0 ? (
                  <button
                    type="button"
                    onClick={() => navigate("/tasks")}
                    className="rounded-full bg-amber-500/10 px-1.5 py-px text-[10.5px] font-medium text-amber-700 hover:bg-amber-500/20"
                    title={`${tasks} task${tasks === 1 ? "" : "s"} due`}
                  >
                    {tasks} task{tasks === 1 ? "" : "s"}
                  </button>
                ) : null}
                {invoices > 0 ? (
                  <button
                    type="button"
                    onClick={() => navigate("/invoices")}
                    className="rounded-full bg-red-500/10 px-1.5 py-px text-[10.5px] font-medium text-red-700 hover:bg-red-500/20"
                    title={`${invoices} invoice${invoices === 1 ? "" : "s"} due`}
                  >
                    {invoices} inv
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-[56px_repeat(7,1fr)]">
          <div className="relative">
            {hours.map((hour) => (
              <div
                key={hour}
                style={{ height: hourHeight }}
                className="pr-2 text-right text-[10.5px] text-muted-foreground"
              >
                {new Date(2000, 0, 1, hour).toLocaleTimeString(undefined, {
                  hour: "numeric",
                })}
              </div>
            ))}
          </div>
          {days.map((day) => (
            <div
              key={day.toISOString()}
              className={cn(
                "relative border-l border-border-subtle",
                isSameDay(day, new Date()) && "bg-surface-muted/50",
              )}
              style={{ height: hourHeight * hours.length }}
            >
              {hours.map((hour) => (
                <div
                  key={hour}
                  style={{ height: hourHeight }}
                  className="border-b border-border-subtle/60"
                />
              ))}
              {entryBlocks(day).map((block) => (
                <button
                  key={block.key}
                  type="button"
                  onClick={block.onClick}
                  style={{
                    top: (block.startHour - DAY_START_HOUR) * hourHeight + 1,
                    height: Math.max(block.durationHours * hourHeight - 2, 20),
                  }}
                  className={cn(
                    "absolute inset-x-0.5 z-10 overflow-hidden rounded-md px-1.5 py-0.5 text-left text-[11px] leading-tight transition-opacity hover:opacity-90",
                    block.kind === "appointment"
                      ? "bg-primary text-primary-foreground"
                      : "border-2 border-primary/50 bg-card text-foreground",
                    block.cancelled && "opacity-40 line-through",
                  )}
                >
                  <span className="block truncate font-medium">
                    {block.title}
                  </span>
                  <span
                    className={cn(
                      "block truncate",
                      block.kind === "appointment"
                        ? "text-primary-foreground/70"
                        : "text-muted-foreground",
                    )}
                  >
                    {block.time}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </Card>
      <p className="mt-2 flex items-center gap-1 text-[12px] text-muted-foreground">
        <Link2 className="size-3.5" />
        Customers can self-book through your public booking pages, manage them
        from the button above.
      </p>
    </>
  );
};
